import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminCancellationPolicySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    cancellationHoursOffset: ''
  });

  useEffect(() => {
    fetchCancellationSettings();
  }, []);

  const fetchCancellationSettings = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getCancellationPolicySettings();
      if (response.success) {
        setSettings({
          cancellationHoursOffset: response.data.cancellationHoursOffset || ''
        });
      } else {
        toast.error(response.message || 'Failed to fetch cancellation policy settings');
      }
    } catch (error) {
      console.error('Error fetching cancellation policy settings:', error);
      toast.error('Failed to fetch cancellation policy settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await adminApiService.updateCancellationPolicySettings({
        cancellationHoursOffset: parseInt(settings.cancellationHoursOffset)
      });
      
      if (response.success) {
        toast.success('Cancellation policy settings updated successfully!');
      } else {
        toast.error(response.message || 'Failed to update cancellation policy settings');
      }
    } catch (error) {
      console.error('Error updating cancellation policy settings:', error);
      toast.error('Failed to update cancellation policy settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getTimeDescription = (hours) => {
    if (!hours || hours === '') return '';
    const h = parseInt(hours);
    if (h === 0) return 'Immediate cancellation allowed';
    if (h === 1) return '1 hour before check-in';
    if (h < 24) return `${h} hours before check-in`;
    if (h === 24) return '1 day before check-in';
    const days = Math.floor(h / 24);
    const remainingHours = h % 24;
    if (remainingHours === 0) return `${days} day${days > 1 ? 's' : ''} before check-in`;
    return `${days} day${days > 1 ? 's' : ''} and ${remainingHours} hour${remainingHours > 1 ? 's' : ''} before check-in`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cancellation Policy Settings</h1>
            <p className="text-gray-600 mt-2">Configure cancellation time limits and policies</p>
          </div>
          <Link 
            to="/admin/dashboard/settings" 
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            ← Back to Settings
          </Link>
        </div>
      </div>

      {/* Settings Form */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Cancellation Hours Offset Settings
          </h2>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Policy Name (Read-only) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Policy Name
                </label>
                <input
                  type="text"
                  value="Cancellation Hours Offset"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  readOnly
                />
                <p className="mt-1 text-sm text-gray-500">This policy name cannot be changed.</p>
              </div>

              {/* Cancellation Hours Offset */}
              <div>
                <label htmlFor="cancellationHoursOffset" className="block text-sm font-semibold text-gray-700 mb-2">
                  Cancellation Offset
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="cancellationHoursOffset"
                    name="cancellationHoursOffset"
                    value={settings.cancellationHoursOffset}
                    onChange={handleInputChange}
                    min="0"
                    step="1"
                    className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter number of hours"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-gray-500 text-sm">hrs</span>
                  </div>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Enter the number of hours before which cancellation is allowed.
                </p>
              </div>
            </div>

            {/* Policy Description */}
            {settings.cancellationHoursOffset && (
              <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h3 className="text-sm font-semibold text-orange-800 mb-2">Policy Description</h3>
                <p className="text-sm text-orange-700">
                  Customers can cancel their bookings up to{' '}
                  <span className="font-semibold">{getTimeDescription(settings.cancellationHoursOffset)}</span>.
                  After this time, cancellations may not be allowed or may incur penalties.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className={`px-6 py-2 rounded-md font-semibold text-white transition-colors duration-200 ${
                  saving 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500'
                }`}
              >
                {saving ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Settings
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Examples Section */}
      <div className="mt-6 bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Common Cancellation Policies</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 cursor-pointer"
                 onClick={() => setSettings({...settings, cancellationHoursOffset: '0'})}>
              <div className="font-semibold text-gray-900">Flexible</div>
              <div className="text-sm text-gray-600">0 hours - Immediate cancellation</div>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 cursor-pointer"
                 onClick={() => setSettings({...settings, cancellationHoursOffset: '24'})}>
              <div className="font-semibold text-gray-900">Standard</div>
              <div className="text-sm text-gray-600">24 hours - 1 day before</div>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 cursor-pointer"
                 onClick={() => setSettings({...settings, cancellationHoursOffset: '48'})}>
              <div className="font-semibold text-gray-900">Strict</div>
              <div className="text-sm text-gray-600">48 hours - 2 days before</div>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 cursor-pointer"
                 onClick={() => setSettings({...settings, cancellationHoursOffset: '72'})}>
              <div className="font-semibold text-gray-900">Very Strict</div>
              <div className="text-sm text-gray-600">72 hours - 3 days before</div>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 cursor-pointer"
                 onClick={() => setSettings({...settings, cancellationHoursOffset: '168'})}>
              <div className="font-semibold text-gray-900">Super Strict</div>
              <div className="text-sm text-gray-600">168 hours - 1 week before</div>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 cursor-pointer"
                 onClick={() => setSettings({...settings, cancellationHoursOffset: '6'})}>
              <div className="font-semibold text-gray-900">Same Day</div>
              <div className="text-sm text-gray-600">6 hours - Same day cancellation</div>
            </div>
          </div>
        </div>
      </div>

      {/* Information Section */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-blue-800 mb-1">Important Information</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• This setting affects when customers can cancel their bookings</li>
              <li>• Setting to 0 hours allows immediate cancellation at any time</li>
              <li>• Higher values provide more protection for hotel partners</li>
              <li>• Changes apply to all new bookings immediately</li>
              <li>• Existing bookings follow the policy that was active when they were made</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCancellationPolicySettings;